/**
 * Helpers for the Upcoming IPOs page: DRHP pipeline tabs, filters, the per-company
 * approval journey and CSV export.
 */
import { parseIso } from './ipoCalendar';
import { toLocalIsoDate } from './gmpBoard';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// '2026-03-30' -> '30-Mar-2026'
export const formatFilingDate = (iso) => {
  if (!iso) return null;
  const date = parseIso(iso);
  return `${String(date.getDate()).padStart(2, '0')}-${MONTHS[date.getMonth()]}-${date.getFullYear()}`;
};

export const daysBetween = (fromIso, toIso) => Math.round((parseIso(toIso) - parseIso(fromIso)) / 86400000);

export const addMonths = (iso, months) => {
  const date = parseIso(iso);
  date.setMonth(date.getMonth() + months);
  return toLocalIsoDate(date);
};

export const STATUS_META = {
  approved: { label: 'SEBI Approved', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  under_review: { label: 'Under Review', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  pre_filing: { label: 'Pre-Filing Stage', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
};

export const OFFER_META = {
  fresh: { label: 'Fresh Capital Only', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  fresh_ofs: { label: 'Fresh cum OFS', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  ofs: { label: 'OFS Only', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  pre_filed: { label: 'Pre-filed DRHP', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const PIPELINE_TABS = [
  { key: 'mainboard', label: 'Mainboard IPOs', test: (filing) => filing.board === 'mainboard' },
  { key: 'sme', label: 'SME IPOs', test: (filing) => filing.board === 'sme' },
  { key: 'confidential', label: 'Confidential Filings', test: (filing) => Boolean(filing.confidential) },
  { key: 'approved', label: 'SEBI Approved Queue', test: (filing) => filing.status === 'approved' },
];

export const tabByKey = (key) => PIPELINE_TABS.find((tab) => tab.key === key) || PIPELINE_TABS[0];

export const uniqueSorted = (filings, field) =>
  [...new Set(filings.map((filing) => filing[field]).filter(Boolean))].sort((a, b) => a.localeCompare(b));

export const filterFilings = (filings, { tab = 'mainboard', status = 'all', sector = 'all', leadManager = 'all', query = '' } = {}) => {
  const needle = query.trim().toLowerCase();
  return filings
    .filter(tabByKey(tab).test)
    .filter((filing) => status === 'all' || filing.status === status)
    .filter((filing) => sector === 'all' || filing.sector === sector)
    .filter((filing) => leadManager === 'all' || filing.leadManager === leadManager)
    .filter(
      (filing) =>
        !needle ||
        [filing.company, filing.sector, filing.leadManager].some((text) => text.toLowerCase().includes(needle))
    )
    .sort((a, b) => (b.approvedOn || b.filedOn).localeCompare(a.approvedOn || a.filedOn));
};

/**
 * The four regulatory stages for one company, each marked 'done' | 'current' | 'pending'.
 * SEBI observation letters stay valid for 12 months.
 */
export const buildJourney = (filing, today) => {
  const approved = filing.status === 'approved' && filing.approvedOn;
  const validUntil = approved ? addMonths(filing.approvedOn, 12) : null;
  const reviewDays = approved ? daysBetween(filing.filedOn, filing.approvedOn) : daysBetween(filing.filedOn, today);

  return [
    {
      key: 'filed',
      title: filing.confidential ? 'Confidential pre-filing' : 'DRHP filed',
      detail: formatFilingDate(filing.filedOn),
      state: 'done',
    },
    {
      key: 'review',
      title: 'SEBI review',
      detail: approved ? `Cleared in ${reviewDays} days` : `${Math.max(reviewDays, 0)} days so far`,
      state: approved ? 'done' : 'current',
    },
    {
      key: 'observation',
      title: 'Observation letter',
      detail: approved ? formatFilingDate(filing.approvedOn) : 'Awaiting',
      state: approved ? 'done' : 'pending',
    },
    {
      key: 'rhp',
      title: 'RHP & bidding',
      detail: approved ? `Launch by ${formatFilingDate(validUntil)}` : 'After approval',
      state: approved ? 'current' : 'pending',
    },
  ];
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildFilingsCsv = (filings) => {
  const header = [
    'Company', 'Sector', 'Board', 'Offer Structure', 'DRHP Filing Date', 'Approval Date',
    'Estimated Size', 'Size Note', 'Exchange', 'Lead Manager', 'Status', 'Confidential',
  ];
  const lines = filings.map((filing) =>
    [
      filing.company,
      filing.sector,
      filing.board === 'sme' ? 'SME' : 'Mainboard',
      OFFER_META[filing.offer]?.label,
      filing.filedOn,
      filing.approvedOn,
      filing.size,
      filing.sizeNote,
      filing.exchanges.join(' / '),
      filing.leadManager,
      STATUS_META[filing.status]?.label,
      filing.confidential ? 'Yes' : 'No',
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
